import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Workout from "../models/Workout";
import { WORKOUT_CATEGORIES, getCategoryById } from "../config/workoutCategories";
import { SCORE_TYPES } from "../schemas/workout";

const SCORE_TYPE_OPTIONS = [
  { id: "time",   label: "Time (minutes)" },
  { id: "rounds", label: "Rounds" },
  { id: "reps",   label: "Reps" },
  { id: "weight", label: "Weight (kg)" },
];

const AddWorkoutPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("AMRAP");
  const [scoreType, setScoreType] = useState("rounds");
  const [exercises, setExercises] = useState([{ name: "", targetReps: "", notes: "" }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Timer fields
  const [rounds, setRounds] = useState(null);
  const [intervalSec, setIntervalSec] = useState(null);
  const [capSec, setCapSec] = useState(null);
  const [workSec, setWorkSec] = useState(null);
  const [restSec, setRestSec] = useState(null);

  const getDefaults = (catId) => getCategoryById(catId).timerDefaults || {};

  const handleCategoryChange = (newCat) => {
    setCategory(newCat);
    const td = getDefaults(newCat);
    setRounds(td.rounds ?? null);
    setIntervalSec(td.intervalSec ?? null);
    setCapSec(td.capSec ?? null);
    setWorkSec(td.workSec ?? null);
    setRestSec(td.restSec ?? null);
  };

  const addExercise = () => {
    setExercises([...exercises, { name: "", targetReps: "", notes: "" }]);
  };

  const removeExercise = (index) => {
    if (exercises.length > 1) {
      setExercises(exercises.filter((_, i) => i !== index));
    }
  };

  const updateExercise = (index, field, value) => {
    const updated = [...exercises];
    updated[index] = { ...updated[index], [field]: value };
    setExercises(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Workout name is required");
      return;
    }

    setLoading(true);

    try {
      const filteredExercises = exercises
        .filter(ex => ex.name.trim())
        .map(ex => ({
          name: ex.name.trim(),
          targetReps: ex.targetReps ? parseInt(ex.targetReps, 10) : null,
          notes: ex.notes || "",
        }));

      const data = {
        name,
        description,
        category,
        scoreType,
        exercises: filteredExercises,
      };

      // Add category-specific fields
      if (rounds != null) data.rounds = rounds;
      if (intervalSec != null) data.intervalSec = intervalSec;
      if (capSec != null) data.capSec = capSec;
      if (workSec != null) data.workSec = workSec;
      if (restSec != null) data.restSec = restSec;

      await Workout.create(user.uid, data);
      navigate("/workouts");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const td = getDefaults(category);
  const hasRounds = 'rounds' in td;
  const hasIntervalSec = 'intervalSec' in td;
  const hasCapSec = 'capSec' in td || ['ForTime', 'Endurance', 'Chipper', 'Other'].includes(category);
  const hasWorkSec = 'workSec' in td;
  const hasRestSec = 'restSec' in td;

  const fmtSec = (s) => {
    const n = Number(s);
    if (!n || n <= 0) return null;
    const m = Math.floor(n / 60);
    const r = n % 60;
    return m > 0 ? `${m}:${String(r).padStart(2, "0")} min` : `${r}s`;
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <button
          onClick={() => navigate("/workouts")}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Create Workout
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Define a recurring workout to track over time
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Workout Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., 20 min AMRAP - Burpees & Pullups"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the workout..."
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {WORKOUT_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              What do you track?
            </label>
            <select
              value={scoreType}
              onChange={(e) => setScoreType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {SCORE_TYPE_OPTIONS.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Timer fields based on category */}
        {(hasRounds || hasIntervalSec || hasCapSec || hasWorkSec || hasRestSec) && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            {hasRounds && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rounds
                </label>
                <input
                  type="number"
                  value={rounds ?? ""}
                  onChange={(e) => setRounds(e.target.value ? Number(e.target.value) : null)}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            )}
            {hasIntervalSec && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Interval (seconds)
                </label>
                <input
                  type="number"
                  value={intervalSec ?? ""}
                  onChange={(e) => setIntervalSec(e.target.value ? Number(e.target.value) : null)}
                  min="10"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {fmtSec(intervalSec) && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{fmtSec(intervalSec)}</span>
                )}
              </div>
            )}
            {hasCapSec && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Cap (seconds)
                </label>
                <input
                  type="number"
                  value={capSec ?? ""}
                  onChange={(e) => setCapSec(e.target.value ? Number(e.target.value) : null)}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {fmtSec(capSec) && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{fmtSec(capSec)}</span>
                )}
              </div>
            )}
            {hasWorkSec && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Work (seconds)
                </label>
                <input
                  type="number"
                  value={workSec ?? ""}
                  onChange={(e) => setWorkSec(e.target.value ? Number(e.target.value) : null)}
                  min="5"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            )}
            {hasRestSec && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rest (seconds)
                </label>
                <input
                  type="number"
                  value={restSec ?? ""}
                  onChange={(e) => setRestSec(e.target.value ? Number(e.target.value) : null)}
                  min="5"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        )}

        {/* Exercises */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Exercises (optional)
          </label>
          <div className="space-y-3">
            {exercises.map((exercise, index) => (
              <div key={index} className="flex gap-2 items-start">
                <input
                  type="text"
                  value={exercise.name}
                  onChange={(e) => updateExercise(index, "name", e.target.value)}
                  placeholder="Exercise name"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <input
                  type="number"
                  value={exercise.targetReps}
                  onChange={(e) => updateExercise(index, "targetReps", e.target.value)}
                  placeholder="Reps"
                  className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {exercises.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExercise(index)}
                    className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addExercise}
            className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            + Add exercise
          </button>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate("/workouts")}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg font-medium transition-colors"
          >
            {loading ? "Creating..." : "Create Workout"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddWorkoutPage;
